import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';

import { UserBadge } from './UserBadge';
import { useTheme } from '@/contexts/ThemeContext';
import { useCurrentUserQuery, useLogoutMutation } from '@/features/user/queries';

export function AppShell() {
  const { theme, toggleTheme } = useTheme();
  const { data: userData } = useCurrentUserQuery();
  const logoutMutation = useLogoutMutation();

  const isBitrixUser =
    userData?.authProvider?.toLowerCase() === 'bitrix24' ||
    userData?.authProvider?.toUpperCase() === 'BITRIX24';
  const isAdmin = userData?.role === 'ADMIN';

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
      window.location.assign('/api/v1/auth/bitrix/start');
    } catch (error) {
      // Даже при ошибке перенаправляем на Bitrix
      window.location.assign('/api/v1/auth/bitrix/start');
    }
  };

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header__brand">
          <Link
            to="/transcriptions"
            className="app-header__home"
            aria-label="AXIUM meet — к журналу встреч"
          >
            <img
              src={theme === 'light' ? '/branding/axium-meet-logo-black.svg' : '/branding/axium-meet-logo-white.svg'}
              alt="AXIUM meet"
              style={{
                height: '40px',
                maxWidth: '200px',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </Link>
          <nav className="app-header__nav" aria-label="Основная навигация">
            <NavLink className="app-header__link" to="/transcriptions" end={false}>
              Журнал встреч
            </NavLink>
            {isAdmin ? (
              <NavLink className="app-header__link" to="/admin/users" end={false}>
                Пользователи
              </NavLink>
            ) : null}
          </nav>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <UserBadge />
          {isBitrixUser && (
            <button
              type="button"
              className="app-header__logout"
              onClick={handleLogout}
              disabled={logoutMutation.isPending}
            >
              {logoutMutation.isPending ? 'Выход...' : 'Выйти'}
            </button>
          )}
          <button
            type="button"
            role="switch"
            aria-checked={theme === 'dark'}
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            aria-label={theme === 'dark' ? 'Включить светлую тему' : 'Включить тёмную тему'}
            className="theme-toggle"
          >
            <span aria-hidden className="theme-toggle__decor">
              <IconMoonTiny muted />
              <IconSunTiny muted />
            </span>
            <span className="theme-toggle__thumb" data-theme={theme}>
              {theme === 'light' ? <IconSunTiny /> : <IconMoonTiny />}
            </span>
          </button>
        </div>
      </header>
      <main className="app-main">
        <Outlet />
      </main>
      <AppFooter />
    </div>
  );
}

function AppFooter() {
  const [hovered, setHovered] = useState(false);

  return (
    <footer className="app-footer" style={{ justifyContent: 'space-between' }}>
      <Link
        to="/about/technical"
        style={{
          fontSize: 12,
          color: 'var(--accent)',
          textDecoration: 'none',
          fontWeight: 600,
        }}
      >
        Техническая информация
      </Link>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}>
        <span>Powered by&nbsp;</span>
        <a
          href="https://www.web-axioma.ru/"
          target="_blank"
          rel="noreferrer"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            color: 'var(--accent)',
            fontWeight: 700,
            textDecoration: 'none',
            transform: hovered ? 'translateY(-1px) scale(1.04)' : 'translateY(0) scale(1)',
            textShadow: hovered ? '0 0 14px rgba(37,99,235,0.35)' : 'none',
            transition: 'all 180ms ease',
            letterSpacing: hovered ? 0.5 : 0,
          }}
        >
          AXIUM
        </a>
      </div>
    </footer>
  );
}

function IconSunTiny(props: { muted?: boolean }) {
  const o = props.muted ? 0.28 : 1;
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity={o} aria-hidden>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function IconMoonTiny(props: { muted?: boolean }) {
  const o = props.muted ? 0.28 : 1;
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden opacity={o}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}
